import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

// Reconciled 2026-02-27 to the locked 5-format exclusive model. The
// legacy in_feed / essentials cards were removed here because those
// are separate products managed in Admin → Ad Slots (routes/ads.py),
// not part of this canonical 20-slot inventory. This page is
// currently "parked" — the self-serve portal is toggled off in
// settings and public visitors are redirected/served the correct
// pricing via /sponsor. The strings below are kept correct so if the
// master toggle is ever flipped on the visitor sees the new model.
const SLOT_ORDER = ["leaderboard", "sidebar_tall", "rectangle", "sidebar_sticky", "sponsored_category"];
const SLOT_META = {
  leaderboard: {
    emoji: "🏆",
    label: "Leaderboard · 728 × 90",
    description:
      "Full-width banner below the navigation, the first thing every visitor sees.",
    best_for: "Hotels, car rentals, airlines, high-visibility campaigns",
  },
  sidebar_tall: {
    emoji: "📖",
    label: "Half-Page · 300 × 600",
    description:
      "Tall sidebar creative. Dominant, cannot-miss placement while reading.",
    best_for: "Premium brand campaigns seeking dominant desktop visibility",
  },
  rectangle: {
    emoji: "📌",
    label: "Rectangle · 300 × 250",
    description:
      "Sticky 300×250 banner in the desktop sidebar, visible as readers scroll through the news feed.",
    best_for: "Any local business seeking brand visibility",
  },
  sidebar_sticky: {
    emoji: "📎",
    label: "Sidebar Sticky · 300 × 250",
    description:
      "Follows the reader down the page — every article view is another impression.",
    best_for: "Persistent-visibility campaigns and lead-gen offers",
  },
  sponsored_category: {
    emoji: "🏷️",
    label: "Sponsored Category",
    description:
      "Own a full category (Tourism, Business, Sports…) with your brand shown next to every headline.",
    best_for: "Brand-category alignment — e.g. banks + Business, tour ops + Tourism",
  },
};

const FAQ_ITEMS = [
  {
    q: "How quickly will my ad go live?",
    a: "Your ad will be reviewed and go live within 24 hours of payment confirmation. You will receive an email confirmation when it is active.",
  },
  {
    q: "What image sizes do I need?",
    a: "Leaderboard: 728×90px. Half-Page: 300×600px. Rectangle: 300×250px. Sidebar Sticky: 300×250px. Sponsored Category: 800×450px cover. We accept JPG, PNG and WebP under 2MB.",
  },
  {
    q: "Can I target a specific island?",
    a: "Yes — every slot is booked per-island (Aruba, Curaçao, Bonaire, or St. Maarten). Each slot is exclusive to one sponsor per island for the full term.",
  },
  {
    q: "How much does it cost?",
    a: "$400/month standard, or $200/month as a Launch Partner (book before December 31, 2026). Same flat rate for any of the 5 formats on any of the 4 islands.",
  },
  {
    q: "How do I pay?",
    a: "We accept payment via Sentoo (local Aruban businesses) and credit card. Payment is processed securely before your ad goes live.",
  },
  {
    q: "Can I cancel or change my ad?",
    a: "You can update your ad creative at any time by logging into your advertiser dashboard. Cancellations are handled by contacting hello@islandsdaily.com.",
  },
];

export default function AdvertisePage() {
  const [config, setConfig] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [openFaq, setOpenFaq] = useState(-1);

  useEffect(() => {
    document.title = "Advertise on IslandsDaily — Reach the Dutch Caribbean";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Reach tourists, expats and locals across Aruba, Curaçao, Bonaire and St. Maarten. Book your ad on IslandsDaily — the Caribbean Morning Briefing."
      );
    }
    api.get("/advertise/config")
      .then((r) => setConfig(r.data))
      .catch((e) => setNotFound(e?.response?.status === 404));
  }, []);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="advertise-404">
        <div className="text-center">
          <h1 className="font-serif-display text-3xl text-[#1A2E35]">404</h1>
          <p className="text-[#6B7C82] mt-2">This page is not available.</p>
          <Link to="/" className="text-[#0AADA0] mt-4 inline-block">← Home</Link>
        </div>
      </div>
    );
  }
  if (!config) {
    return <div className="p-10 text-center text-[#6B7C82]">Loading…</div>;
  }

  const priceFor = (slot, weeks) => {
    const p = (config.pricing || []).find(
      (x) => x.slot_type === slot && x.duration_weeks === weeks
    );
    return p ? p.price_usd : null;
  };

  // Under the locked model every format is a flat $400/month, or
  // $200/month for Launch Partners (book before Dec 31 2026). We
  // prefer the config values so a future admin edit of the pricing
  // matrix (via the Self-Serve tab) still flows through here.
  const standardPrice = config.standard_price_usd ?? 400;
  const launchPrice = config.launch_partner_price_usd ?? 200;
  const launchActive = !!config.launch_partner_active;

  return (
    <div className="min-h-screen bg-white" data-testid="advertise-page">
      <Header />

      {/* Hero */}
      <section className="bg-[#1A2E35] py-16 px-4" data-testid="advertise-hero">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="font-serif-display text-white" style={{ fontSize: 36, lineHeight: 1.15 }}>
            Reach the Dutch Caribbean
          </h1>
          <p className="text-white/80 text-[16px] max-w-3xl mx-auto mt-4">
            Advertise on IslandsDaily — the Caribbean Morning Briefing reaching Aruba, Curaçao, Bonaire and St. Maarten in English, Dutch and Spanish.
          </p>
          <div className="flex flex-wrap gap-2 justify-center mt-6">
            {["4 Islands", "3 Languages", "Updated Every 15 Minutes", "WhatsApp Digest"].map((p) => (
              <span
                key={p}
                className="bg-[#0AADA0] text-white text-[12px] px-3 py-1 rounded-full"
                data-testid={`hero-stat-${p.split(" ")[0].toLowerCase()}`}
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Slots */}
      <section className="max-w-5xl mx-auto py-12 px-4" data-testid="advertise-slots">
        <h2 className="font-serif-display text-[#1A2E35]" style={{ fontSize: 24 }}>
          Choose Your Placement
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          {SLOT_ORDER.map((slot) => {
            const m = SLOT_META[slot];
            return (
              <div
                key={slot}
                className="border border-[#EEEEEE] rounded-xl p-5 bg-white"
                data-testid={`slot-card-${slot}`}
              >
                <div className="flex items-start gap-3">
                  <span style={{ fontSize: 28 }}>{m.emoji}</span>
                  <div className="flex-1">
                    <h3 className="font-medium text-[#1A2E35] text-[16px]">{m.label}</h3>
                    <p className="text-[13px] text-[#6B7C82] mt-1">{m.description}</p>
                    <p className="text-[12px] text-[#0AADA0] mt-2 italic">
                      Best for: {m.best_for}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3 flex-wrap text-[12px] text-[#6B7C82]">
                  <span className="bg-[#F5F5F5] px-2 py-1 rounded">
                    Standard: <b>${standardPrice}</b>/month
                  </span>
                  {launchActive && (
                    <span className="bg-[#0AADA0]/10 text-[#0AADA0] px-2 py-1 rounded font-medium">
                      Launch Partner: <b>${launchPrice}</b>/month
                    </span>
                  )}
                </div>
                <Link
                  to={`/advertise/book?slot=${slot}`}
                  className="mt-4 inline-block bg-[#E63946] hover:bg-[#cf2f3c] text-white px-4 py-2 rounded-full text-[13px] font-medium"
                  data-testid={`slot-book-${slot}`}
                >
                  Book This Slot →
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing table */}
      <section className="max-w-5xl mx-auto py-12 px-4 border-t border-[#EEEEEE]" data-testid="advertise-pricing">
        <h2 className="font-serif-display text-[#1A2E35]" style={{ fontSize: 24 }}>Pricing</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pricing-table">
          {launchActive && (
            <div className="p-5 rounded-xl border-2 border-[#0AADA0] bg-[#0AADA0]/5 relative">
              <span className="absolute -top-2 left-4 text-[10px] uppercase tracking-wider bg-[#0AADA0] text-white px-2 py-0.5 rounded-full">
                Launch Partner
              </span>
              <div className="text-[11px] uppercase tracking-wider text-[#6B7C82] mt-1">
                Book by {config.launch_partner_end || "Dec 31, 2026"}
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-serif-display text-[32px] text-[#1A2E35] leading-none">
                  ${launchPrice}
                </span>
                <span className="text-[14px] text-[#6B7C82]">/month</span>
              </div>
            </div>
          )}
          <div className={`p-5 rounded-xl border ${launchActive ? "border-[#EEEEEE]" : "border-2 border-[#0AADA0] bg-[#0AADA0]/5"}`}>
            <div className="text-[11px] uppercase tracking-wider text-[#6B7C82]">
              Standard rate
            </div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-serif-display text-[32px] text-[#1A2E35] leading-none">
                ${standardPrice}
              </span>
              <span className="text-[14px] text-[#6B7C82]">/month</span>
            </div>
          </div>
        </div>
        <p className="text-[12px] text-[#6B7C82] mt-3">
          One flat rate — same price for any of the 5 formats, on any of the 4 islands.
          Each slot is exclusive to one sponsor per island for the term of your sponsorship.
        </p>
      </section>

      {/* Testimonials — hidden if none */}
      {(config.testimonials || []).length > 0 && (
        <section
          className="max-w-5xl mx-auto py-12 px-4 border-t border-[#EEEEEE]"
          data-testid="advertise-testimonials"
        >
          <h2 className="font-serif-display text-[#1A2E35]" style={{ fontSize: 24 }}>
            Trusted by Local Businesses
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {config.testimonials.map((t) => (
              <div key={t.id || t.business_name} className="border border-[#EEEEEE] rounded-xl p-4">
                <p className="text-[13px] text-[#1A2E35] italic">“{t.quote}”</p>
                <p className="text-[12px] text-[#6B7C82] mt-2">
                  — {t.business_name}{t.island ? ` · ${t.island}` : ""}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-3xl mx-auto py-12 px-4 border-t border-[#EEEEEE]" data-testid="advertise-faq">
        <h2 className="font-serif-display text-[#1A2E35]" style={{ fontSize: 24 }}>
          Frequently Asked Questions
        </h2>
        <div className="mt-4 divide-y divide-[#EEEEEE]">
          {FAQ_ITEMS.map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={i} className="py-3" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  className="w-full text-left flex justify-between items-center text-[14px] text-[#1A2E35]"
                >
                  <span>{f.q}</span>
                  <span className="text-[#0AADA0]">{open ? "−" : "+"}</span>
                </button>
                {open && (
                  <p className="text-[13px] text-[#6B7C82] mt-2">{f.a}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F5F5F5] py-12 px-4 text-center" data-testid="advertise-cta">
        <p className="font-serif-display text-[#1A2E35]" style={{ fontSize: 22 }}>
          Ready to reach the Dutch Caribbean? Start your campaign today.
        </p>
        <Link
          to="/advertise/book"
          className="mt-4 inline-block bg-[#E63946] hover:bg-[#cf2f3c] text-white px-5 py-2.5 rounded-full text-[14px] font-medium"
          data-testid="cta-book-slot"
        >
          Book a Slot →
        </Link>
        <p className="text-[12px] text-[#6B7C82] mt-2">
          Or contact us: <a href="mailto:hello@islandsdaily.com" className="text-[#0AADA0]">hello@islandsdaily.com</a>
        </p>
      </section>

      <Footer />
    </div>
  );
}
